import { useState } from 'react';
import { useSnackbar } from 'notistack';

import HttpClient from '../../shared/infrastructures/clients/http.client';
import { extractTranslatedApiErrorMessage } from '../../shared/utils/translate-api-error.util';
import { ADMIN_BASKETS_API_PATHS } from '../constants/baskets-api-paths.constants';
import { useUpdateBasket } from './update-basket.hook';

type UseUploadBasketImageResult = {
  uploadBasketImage: (basketId: string, file: File) => Promise<string | null>;
  isSubmitting: boolean;
};

const UPLOAD_ERROR_MESSAGE = "Une erreur est survenue lors du téléversement de l'image";

export function useUploadBasketImage(): UseUploadBasketImageResult {
  const { enqueueSnackbar } = useSnackbar();
  const { updateBasket } = useUpdateBasket();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const uploadBasketImage = async (basketId: string, file: File): Promise<string | null> => {
    setIsSubmitting(true);

    try {
      const { url, key } = await HttpClient.get<{ url: string; key: string }>(ADMIN_BASKETS_API_PATHS.GET_UPLOAD_URL, {
        params: { basketId, contentType: file.type },
      });

      const uploadResponse = await fetch(url, {
        method: 'PUT',
        body: file,
        headers: { 'Content-Type': file.type },
      });

      if (!uploadResponse.ok) {
        enqueueSnackbar(UPLOAD_ERROR_MESSAGE, { variant: 'error' });
        return null;
      }

      await updateBasket({ basketId, basketData: { photo: key } });

      return key;
    } catch (error) {
      enqueueSnackbar(extractTranslatedApiErrorMessage(error, UPLOAD_ERROR_MESSAGE), { variant: 'error' });
      return null;
    } finally {
      setIsSubmitting(false);
    }
  };

  return { uploadBasketImage, isSubmitting };
}
